import React, { createContext, useContext, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Tabs from '../../../../../../Common/Tabs/Tabs';
import OrdersFilters from '../OpenOrders/OpenOrdersFilter/OpenOrdersFilter';
import LimitBuy from './LimitBuy/LimitBuy';
import LimitSell from './LimitSell/LimitSell';
import StopLoss from './StopLoss/StopLoss';
import { usePromise } from '../../../../../../../hooks/usePromise/usePromise';
import { OrdersContext } from '../../../../../../Modals/EditTokensModal/EditOrderDetails';
import {
  createTradeOrder,
  hordToEth,
  parseResponseSignature,
  updateTradeOrder,
} from '../../../../../../../services/hpoolService';
import { useTokenPrice } from '../../../../../../../hooks/useTokenPrice';
import { useEthConverter } from '../../../../../../../hooks/useConverter';
import { multiTypePoolManager } from '../../../../../../../constants/vpool';
import OpenOrder from './VpoolOrders/OpenOrder/OpenOrder';
import CloseOrder from './VpoolOrders/CloseOrder/CloseOrder';
import ClosePortion from './VpoolOrders/ClosePortion/ClosePortion';

const TabPicker = ({ tabs = [], setCurrentTab, currentTab }) => (
  <OrdersFilters filters={tabs} onChange={(id) => setCurrentTab(id)} currentFilter={currentTab} />
);

TabPicker.propTypes = {
  tabs: PropTypes.array.isRequired,
  setCurrentTab: PropTypes.func.isRequired,
  currentTab: PropTypes.string.isRequired,
};

export const TabsContext = createContext({
  onSubmit: () => {},
  loading: false,
  hordUsdPrice: {},
});

function NewOrder({ onSuccess, isVpool, ...rest }) {
  // you can destructure setCurrentTab prop since it's a Tabs.Tab child
  const { setCurrentTab = () => {} } = rest || {};

  const { selectedToken } = useContext(OrdersContext);
  const [, usdToEth, , hordToolKit] = useEthConverter();

  const [createFutureOrder, , loading] = usePromise(createTradeOrder, { showError: true });
  const [updateReq, , updateLoading] = usePromise(updateTradeOrder, { method: 'PUT', showError: true });

  const hordUsdPrice = useTokenPrice(selectedToken);

  const { token_id: tokenId, hpool_id: hpoolId } = selectedToken || {};

  const foramttedDate = (date) => new Date(date).getTime() / 1000;

  const formatAmount = (values) => ({
    amount_source: hordToEth({
      value: values.amount_source,
      hordToUsd: hordToolKit.hordToUsd,
      usdToEth,
    }),
  });

  const moveToOpenOrders = () => setCurrentTab('openOrders');

  const updateOrder = async (_reqBody, response = {}) => {
    // Get future order signature
    const signature = await parseResponseSignature(response);
    const {
      trade_order: { id },
    } = response;
    const reqBody = {
      ..._reqBody,
      signature,
    };
    updateReq(id, reqBody).then(moveToOpenOrders);
  };

  const onSubmit = async (values, type) => {
    const reqBody = {
      slippage: 2.5,
      ...values,
      token_id: tokenId,
      valid_until: foramttedDate(values.valid_until),
      hpool_id: hpoolId,
      type,
    };
    createFutureOrder(reqBody).then((res) => updateOrder(reqBody, res));
  };

  const mergedLoading = [loading, updateLoading].some((isLoading) => isLoading);
  const contextValue = { onSubmit, loading: mergedLoading, hordUsdPrice, hordToEth, formatAmount };
  const renderedTabs = multiTypePoolManager(isVpool).newOrderTabs;
  return (
    <div className="new-order-wrapper">
      <TabsContext.Provider value={contextValue}>
        <Tabs initCurrentTab={renderedTabs[0].id} tabs={renderedTabs} TabPicker={TabPicker}>
          {/* Hpool Order */}
          <Tabs.Tab tab="buyLimit">
            <LimitBuy type="buy_limit" />
          </Tabs.Tab>
          <Tabs.Tab tab="sellLimit">
            <LimitSell type="sell_limit" />
          </Tabs.Tab>
          <Tabs.Tab tab="stopLoss">
            <StopLoss type="stop_loss" />
          </Tabs.Tab>

          {/* Vpool Order */}
          <Tabs.Tab tab="openOrder">
            <OpenOrder />
          </Tabs.Tab>
          <Tabs.Tab tab="closeOrder">
            <CloseOrder />
          </Tabs.Tab>
          <Tabs.Tab tab="closePortion">
            <ClosePortion />
          </Tabs.Tab>
        </Tabs>
      </TabsContext.Provider>
    </div>
  );
}

const mapStateToProps = ({ createHPool }) => ({
  tradeOrders: createHPool.tradeOrders,
  isVpool: createHPool.hpool.is_vpool,
});
NewOrder.propTypes = {
  onSuccess: PropTypes.func,
  isVpool: PropTypes.bool.isRequired,
};

NewOrder.defaultProps = {
  onSuccess: () => {},
};

export default connect(mapStateToProps)(NewOrder);
